import {
	faCheckCircle,
	faExclamationTriangle,
	faPhoneVolume,
	faSpinner,
} from '@fortawesome/pro-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
	Dialog,
	DialogPanel,
	DialogTitle,
	Transition,
	TransitionChild,
} from '@headlessui/react';
import { VonageQueryService } from '@supply-mover-2-0/core';
import React, { Fragment, useCallback, useState } from 'react';
import { useThemeColors } from '../../providers/ThemeProvider';
import { useTranslation } from '../../providers/TranslationProvider';
import { Button } from '../forms/Button';

export interface VonageExtensionSetupModalProps {
	isOpen: boolean;
	onClose: () => void;
	onSetupComplete: (extensionNumber: string) => void;
}

type SetupState = 'initial' | 'loading' | 'success' | 'error';

export function VonageExtensionSetupModal({
	isOpen,
	onClose,
	onSetupComplete,
}: VonageExtensionSetupModalProps): React.ReactElement {
	const colors = useThemeColors();
	const { t } = useTranslation();
	const [setupState, setSetupState] = useState<SetupState>('initial');
	const [errorMessage, setErrorMessage] = useState<string | null>(null);
	const [extensionNumber, setExtensionNumber] = useState<string | null>(null);

	const handleConnectWithVonage = useCallback(async () => {
		setSetupState('loading');
		setErrorMessage(null);

		try {
			// Get the OAuth URL from the backend
			const authData = await VonageQueryService.getUserAuthUrl();

			if (authData.url.trim() === '') {
				setSetupState('error');
				setErrorMessage(
					t('Failed to get authorization URL. Please try again.'),
				);
				return;
			}

			// Open the OAuth popup
			const popup = window.open(
				authData.url,
				'vonage-user-auth',
				'width=600,height=700,scrollbars=yes,resizable=yes',
			);

			if (popup == null) {
				setSetupState('error');
				setErrorMessage(
					t(
						'Popup was blocked. Please allow popups for this site and try again.',
					),
				);
				return;
			}

			// Listen for the OAuth callback
			// The OAuth handler already called the API, so we just need to check the result
			const handleMessage = (event: MessageEvent): void => {
				// Verify the origin matches our domain
				if (event.origin !== window.location.origin) {
					return;
				}

				const data = event.data as {
					type?: string;
					success?: boolean;
					error?: string;
				};

				if (data.type !== 'vonage-user-oauth-callback') {
					return;
				}

				// Remove the event listener
				window.removeEventListener('message', handleMessage);

				// Close the popup
				popup.close();

				if (data.success !== true) {
					setSetupState('error');
					setErrorMessage(
						data.error ?? t('Failed to set up extension. Please try again.'),
					);
					return;
				}

				// Success! Fetch the updated status to get the extension info
				void (async (): Promise<void> => {
					try {
						const status = await VonageQueryService.getUserDialStatus(true);

						if (status.userExtension != null) {
							setExtensionNumber(status.userExtension);
							setSetupState('success');
						} else {
							setSetupState('error');
							setErrorMessage(
								t(
									'Extension setup completed but no extension was found. Please try again.',
								),
							);
						}
					} catch {
						setSetupState('error');
						setErrorMessage(
							t('Failed to verify extension setup. Please try again.'),
						);
					}
				})();
			};

			window.addEventListener('message', handleMessage);

			// Also poll for popup close (in case user closes it manually)
			const pollTimer = setInterval(() => {
				if (popup.closed) {
					clearInterval(pollTimer);
					window.removeEventListener('message', handleMessage);

					// Only set error if we haven't received a success callback
					if (setupState === 'loading') {
						setSetupState('initial');
					}
				}
			}, 500);
		} catch (error) {
			console.error('Error initiating Vonage OAuth:', error);
			setSetupState('error');
			setErrorMessage(t('An unexpected error occurred. Please try again.'));
		}
	}, [t, setupState]);

	const handleClose = (): void => {
		if (setupState === 'success' && extensionNumber != null) {
			onSetupComplete(extensionNumber);
		}
		onClose();
		// Reset state after close animation
		setTimeout(() => {
			setSetupState('initial');
			setErrorMessage(null);
			setExtensionNumber(null);
		}, 300);
	};

	const handleContinue = (): void => {
		if (extensionNumber != null) {
			onSetupComplete(extensionNumber);
		}
		onClose();
		// Reset state after close animation
		setTimeout(() => {
			setSetupState('initial');
			setErrorMessage(null);
			setExtensionNumber(null);
		}, 300);
	};

	return (
		<Transition appear show={isOpen} as={Fragment}>
			<Dialog as="div" className="relative z-50" onClose={handleClose}>
				<TransitionChild
					as={Fragment}
					enter="ease-out duration-300"
					enterFrom="opacity-0"
					enterTo="opacity-100"
					leave="ease-in duration-200"
					leaveFrom="opacity-100"
					leaveTo="opacity-0"
				>
					<div className="fixed inset-0 bg-black bg-opacity-25" />
				</TransitionChild>

				<div className="fixed inset-0 overflow-y-auto">
					<div className="flex min-h-full items-center justify-center p-4 text-center">
						<TransitionChild
							as={Fragment}
							enter="ease-out duration-300"
							enterFrom="opacity-0 scale-95"
							enterTo="opacity-100 scale-100"
							leave="ease-in duration-200"
							leaveFrom="opacity-100 scale-100"
							leaveTo="opacity-0 scale-95"
						>
							<DialogPanel
								className={`w-full max-w-md transform rounded-lg ${colors.classes.bgPrimary} text-left align-middle shadow-xl transition-all`}
							>
								<div
									className={`px-6 py-4 border-b ${colors.classes.borderDefault}`}
								>
									<DialogTitle
										as="h3"
										className={`text-lg font-medium leading-6 ${colors.classes.textPrimary}`}
									>
										{t('Set Up Vonage Extension')}
									</DialogTitle>
								</div>

								<div className="px-6 py-6">
									{setupState === 'initial' ? (
										<div className="text-center space-y-6">
											<div
												className={`w-16 h-16 mx-auto rounded-full flex items-center justify-center ${colors.classes.bgSuccess}`}
											>
												<FontAwesomeIcon
													icon={faPhoneVolume}
													className="text-white text-2xl"
												/>
											</div>
											<div>
												<h4
													className={`text-lg font-semibold mb-2 ${colors.classes.textPrimary}`}
												>
													{t('Connect Your Vonage Account')}
												</h4>
												<p
													className={`text-sm ${colors.classes.textSecondary}`}
												>
													{t(
														'To make calls via Vonage, you need to connect your Vonage account. Click the button below to sign in with your Vonage credentials.',
													)}
												</p>
											</div>
											<Button
												variant="primary"
												onClick={() => {
													void handleConnectWithVonage();
												}}
												className="w-full"
											>
												<FontAwesomeIcon
													icon={faPhoneVolume}
													className="mr-2"
												/>
												{t('Connect with Vonage')}
											</Button>
											<p
												className={`text-xs ${colors.classes.textMuted} italic`}
											>
												{t(
													'Your Vonage extension will be automatically detected based on the account you sign into.',
												)}
											</p>
										</div>
									) : null}

									{setupState === 'loading' ? (
										<div className="text-center space-y-4 py-8">
											<FontAwesomeIcon
												icon={faSpinner}
												className="text-4xl animate-spin"
												style={{ color: colors.values.primary }}
											/>
											<p className={colors.classes.textSecondary}>
												{t('Connecting to Vonage...')}
											</p>
										</div>
									) : null}

									{setupState === 'success' ? (
										<div className="text-center space-y-6">
											<div
												className={`w-16 h-16 mx-auto rounded-full flex items-center justify-center ${colors.classes.bgSuccess}`}
											>
												<FontAwesomeIcon
													icon={faCheckCircle}
													className="text-white text-2xl"
												/>
											</div>
											<div>
												<h4
													className={`text-lg font-semibold mb-2 ${colors.classes.textPrimary}`}
												>
													{t('Extension Connected!')}
												</h4>
												<p
													className={`text-sm ${colors.classes.textSecondary}`}
												>
													{t('Your Vonage extension')} {extensionNumber}{' '}
													{t('has been connected successfully.')}
												</p>
											</div>
											<Button
												variant="primary"
												onClick={handleContinue}
												className="w-full"
											>
												{t('Continue')}
											</Button>
										</div>
									) : null}

									{setupState === 'error' ? (
										<div className="text-center space-y-6">
											<div
												className={`w-16 h-16 mx-auto rounded-full flex items-center justify-center ${colors.classes.bgWarning}`}
											>
												<FontAwesomeIcon
													icon={faExclamationTriangle}
													className="text-white text-2xl"
												/>
											</div>
											<div>
												<h4
													className={`text-lg font-semibold mb-2 ${colors.classes.textPrimary}`}
												>
													{t('Setup Failed')}
												</h4>
												<p
													className={`text-sm ${colors.classes.textSecondary}`}
												>
													{errorMessage ??
														t(
															'An error occurred while setting up your Vonage extension.',
														)}
												</p>
											</div>
											<div className="flex gap-3">
												<Button
													variant="outline"
													onClick={handleClose}
													className="flex-1"
												>
													{t('Cancel')}
												</Button>
												<Button
													variant="primary"
													onClick={() => {
														setSetupState('initial');
														setErrorMessage(null);
													}}
													className="flex-1"
												>
													{t('Try Again')}
												</Button>
											</div>
										</div>
									) : null}
								</div>

								{setupState === 'initial' || setupState === 'loading' ? (
									<div
										className={`flex justify-end px-6 py-4 border-t ${colors.classes.borderDefault}`}
									>
										<Button
											variant="outline"
											onClick={handleClose}
											disabled={setupState === 'loading'}
										>
											{t('Cancel')}
										</Button>
									</div>
								) : null}
							</DialogPanel>
						</TransitionChild>
					</div>
				</div>
			</Dialog>
		</Transition>
	);
}
